/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { AppConfigService, AuthenticationService } from '@alfresco/adf-core';
import { NodesApi } from '@alfresco/js-api';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

// The shapes mirror the JSON the sidecar (services/aca-dms-jobs) reads — see
// Jobs/JobDefinition.cs there. Definitions and run reports are plain content
// files in the repository, so CRUD and history go through the nodes API with
// the administrator's own session; the sidecar is only asked for the things
// the repository cannot answer — next-run times, a dry-run preview, and
// "run it now".

export interface JobRule {
  field: string;
  op: string;
  value?: string;
  valueTo?: string;
}

export interface JobDefinition {
  /** Node id of the JSON file the definition is stored in — the job's id. */
  id?: string;
  name: string;
  enabled: boolean;
  schedule: { cron: string; timezone: string };
  condition: { nodeType: string; rules: JobRule[]; extraQuery?: string };
  action: {
    type: string;
    path?: string;
    days?: number;
    date?: string;
    tag?: string;
    permanent?: boolean;
    template?: string;
    recipients?: string[];
    script?: string;
  };
  notify: {
    onComplete: boolean;
    onlyWhenMatched: boolean;
    recipients: string[];
    template?: string;
    attachList?: boolean;
  };
  /** Rehearsal mode: count and report, but never change the repository. */
  dryRun?: boolean;
  batchSize?: number;
  maxItemsPerRun?: number;
}

export interface JobRunError {
  nodeId: string;
  name: string;
  message: string;
}

/** One matched document's outcome — status: "processed" | "skipped" | "failed". */
export interface JobRunItem {
  nodeId: string;
  name: string;
  path: string;
  size?: number | null;
  mimeType?: string | null;
  modified?: string | null;
  status: string;
  error?: string | null;
}

/** One finished run, as written under DMS Jobs/Runs. */
export interface JobRun {
  nodeId?: string;
  jobId: string;
  jobName: string;
  action: string;
  triggeredBy: string;
  dryRun?: boolean;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  matched: number;
  processed: number;
  errors: JobRunError[];
  emailed?: string[];
  /** The AFTS query the run actually used — recorded verbatim in the report. */
  query?: string;
  /** Names of the first matched documents (the sidecar caps this list). */
  sample?: string[];
  /** Every document the run touched, with its location and outcome (capped at 1000 in the report). */
  items?: JobRunItem[];
}

/** What the sidecar reports about a definition it knows: when it next fires. */
export interface JobScheduleState {
  id: string;
  name: string;
  enabled: boolean;
  nextRun: string | null;
  running: boolean;
}

export interface JobPreview {
  query: string;
  matched: number;
  sample: { id: string; name: string; path: string }[];
}

@Injectable({ providedIn: 'root' })
export class JobsService {
  private readonly api = inject(AlfrescoApiService);
  private readonly http = inject(HttpClient);
  private readonly appConfig = inject(AppConfigService);
  private readonly auth = inject(AuthenticationService);

  private static readonly FOLDER = 'Data Dictionary/DMS Jobs';
  private static readonly RUNS = 'Data Dictionary/DMS Jobs/Runs';

  private get nodes(): NodesApi {
    return new NodesApi(this.api.getInstance() as any);
  }

  private get baseUrl(): string {
    return this.appConfig.get<string>('jobs.baseUrl', '/jobs-api');
  }

  // ── definitions (plain JSON files in the Data Dictionary) ─────────────

  /** Every *.json in "DMS Jobs", parsed into JobDefinition. */
  list(): Observable<JobDefinition[]> {
    return this.folderId(JobsService.FOLDER).pipe(
      switchMap((folderId) => from(this.nodes.listNodeChildren(folderId, { maxItems: 500 } as any) as Promise<any>)),
      switchMap((res: any) => {
        const files: any[] = (res?.list?.entries || []).map((e: any) => e.entry).filter((n: any) => n.isFile && n.name?.endsWith('.json'));
        if (!files.length) {
          return of([] as JobDefinition[]);
        }
        return forkJoin(
          files.map(
            (f: any): Observable<JobDefinition | null> =>
              this.readText(f.id).pipe(
                map((text): JobDefinition | null => {
                  try {
                    const def = JSON.parse(text) as JobDefinition;
                    def.id = f.id;
                    def.name = def.name || f.name.replace(/\.json$/i, '');
                    return def;
                  } catch {
                    return null;
                  }
                }),
                catchError(() => of(null))
              )
          )
        ).pipe(map((defs) => defs.filter((d): d is JobDefinition => d !== null)));
      })
    );
  }

  /** Creates the file for a new job or overwrites an existing one. Returns the node id. */
  save(job: JobDefinition): Observable<string> {
    const { id, ...def } = job;
    const json = JSON.stringify(def, null, 2);

    if (id) {
      return from(this.nodes.updateNodeContent(id, json) as Promise<any>).pipe(map(() => id));
    }

    return this.folderId(JobsService.FOLDER).pipe(
      switchMap((folderId) =>
        from(this.nodes.createNode(folderId, { name: this.fileName(job.name), nodeType: 'cm:content' } as any) as Promise<any>)
      ),
      switchMap((res: any) => from(this.nodes.updateNodeContent(res.entry.id, json) as Promise<any>).pipe(map(() => res.entry.id as string)))
    );
  }

  remove(id: string): Observable<any> {
    return from(this.nodes.deleteNode(id, { permanent: true } as any) as Promise<any>);
  }

  // ── run history ───────────────────────────────────────────────────────

  /** Newest-first run reports — the JSON the sidecar writes after every run. */
  runs(maxItems = 50): Observable<JobRun[]> {
    return this.folderId(JobsService.RUNS).pipe(
      switchMap((folderId) => from(this.nodes.listNodeChildren(folderId, { maxItems, orderBy: ['createdAt DESC'] } as any) as Promise<any>)),
      switchMap((res: any) => {
        const files: any[] = (res?.list?.entries || []).map((e: any) => e.entry).filter((n: any) => n.isFile);
        if (!files.length) {
          return of([] as JobRun[]);
        }
        return forkJoin(
          files.map(
            (f: any): Observable<JobRun | null> =>
              this.readText(f.id).pipe(
                map((text): JobRun | null => {
                  try {
                    const run = JSON.parse(text) as JobRun;
                    run.nodeId = f.id;
                    return run;
                  } catch {
                    return null;
                  }
                }),
                catchError(() => of(null))
              )
          )
        ).pipe(map((runs) => runs.filter((r): r is JobRun => r !== null)));
      }),
      catchError(() => of([] as JobRun[]))
    );
  }

  // ── sidecar ───────────────────────────────────────────────────────────

  /** Next-run times and running flags — the part only the scheduler knows. */
  scheduleState(): Observable<JobScheduleState[]> {
    return this.http.get<JobScheduleState[]>(`${this.baseUrl}/api/jobs`, { headers: this.credentials() }).pipe(catchError(() => of([])));
  }

  /** Whether the sidecar is up and whether it can send mail. */
  health(): Observable<{ status: string; mail: boolean; jobs: number } | null> {
    return this.http.get<{ status: string; mail: boolean; jobs: number }>(`${this.baseUrl}/api/jobs/health`).pipe(catchError(() => of(null)));
  }

  /** Dry-run a definition — saved or in-flight — and get the query + count + sample. */
  preview(job: JobDefinition): Observable<JobPreview> {
    return this.http.post<JobPreview>(`${this.baseUrl}/api/jobs/preview`, job, { headers: this.credentials() });
  }

  runNow(id: string): Observable<boolean> {
    return this.http.post(`${this.baseUrl}/api/jobs/${encodeURIComponent(id)}/run`, null, { headers: this.credentials() }).pipe(map(() => true));
  }

  // ── helpers ────────────────────────────────────────────────────────────

  private readText(nodeId: string): Observable<string> {
    return from(this.nodes.getNodeContent(nodeId) as Promise<any>).pipe(
      switchMap((data: any) => from(typeof data?.text === 'function' ? (data.text() as Promise<string>) : Promise.resolve(String(data ?? ''))))
    );
  }

  /** A Company-Home-relative path → node id, creating missing folders along the way. */
  private folderId(path: string): Observable<string> {
    const segments = path.split('/').filter(Boolean);
    const walk = (parentId: string, rest: string[], walked: string[]): Observable<string> => {
      if (!rest.length) {
        return of(parentId);
      }
      const next = [...walked, rest[0]].join('/');
      return from(this.nodes.getNode('-root-', { relativePath: next } as any) as Promise<any>).pipe(
        map((res: any) => res.entry.id as string),
        catchError(() =>
          from(this.nodes.createNode(parentId, { name: rest[0], nodeType: 'cm:folder' } as any) as Promise<any>).pipe(
            map((res: any) => res.entry.id as string)
          )
        ),
        switchMap((id) => walk(id, rest.slice(1), [...walked, rest[0]]))
      );
    };
    return walk('-root-', segments, []);
  }

  private fileName(name: string): string {
    const slug = (name || 'job')
      .replace(/[^\p{L}\p{N}]+/gu, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);
    return `${slug || 'job'}-${Date.now().toString(36)}.json`;
  }

  /** The sidecar asks Alfresco on the caller's behalf, so a ticket goes along. */
  private credentials(): HttpHeaders {
    const ticket = this.auth.getToken();
    return ticket ? new HttpHeaders({ Authorization: 'Basic ' + btoa(`ROLE_TICKET:${ticket}`) }) : new HttpHeaders();
  }
}
